'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import type { Lang } from '@/lib/langs';
import { redactCheckoutSecrets } from '@/lib/analytics';

const KEY = 'la_analytics_consent_v1';
const ID = 'G-9K61Y80X9F';
const MAX_AGE = 180 * 24 * 60 * 60 * 1000;
const SIGNAL_COPY: Record<Lang, string> = {
  ja: 'ブラウザのGlobal Privacy Control設定により、解析は停止しています。',
  en: 'Analytics is off because Global Privacy Control is enabled in your browser.',
  ko: '브라우저의 Global Privacy Control 설정으로 분석이 중지되어 있습니다.',
  zh: '浏览器已启用 Global Privacy Control，因此分析已关闭。',
  es: 'El análisis está desactivado porque Global Privacy Control está activo en tu navegador.',
  fr: 'La mesure est désactivée car Global Privacy Control est actif dans votre navigateur.',
  de: 'Die Analyse ist deaktiviert, weil Global Privacy Control in Ihrem Browser aktiv ist.',
  pt: 'A análise está desativada porque o Global Privacy Control está ativo no navegador.',
};
// This controls analytics only. Advertising consent is handled separately by
// Google's certified CMP, configured in AdSense Privacy & messaging.
const COPY: Record<Lang, [string, string, string, string, string]> = {
  ja: ['アクセス解析の設定', "使いやすさの改善にGoogle Analyticsを使用します。解析を許可・拒否できます。拒否してもツールは使えます。", '解析を許可', '解析を拒否', 'プライバシー'],
  en: ['Analytics settings', "We use Google Analytics to improve the tools. You can allow or decline analytics and keep using the tools.", 'Allow analytics', 'Decline analytics', 'Privacy'],
  ko: ['분석 설정', "도구 개선을 위해 Google Analytics를 사용합니다. 분석을 허용하거나 거부해도 도구를 계속 사용할 수 있습니다.", '분석 허용', '분석 거부', '개인정보'],
  zh: ['访问分析设置', "我们使用 Google Analytics 改善工具。您可以允许或拒绝分析，拒绝不会影响使用。", '允许分析', '拒绝分析', '隐私'],
  es: ['Ajustes de análisis', "Usamos Google Analytics para mejorar las herramientas. Puedes permitir o rechazar el análisis y seguir usándolas.", 'Permitir análisis', 'Rechazar análisis', 'Privacidad'],
  fr: ['Paramètres de mesure', "Nous utilisons Google Analytics pour améliorer les outils. Vous pouvez accepter ou refuser la mesure et continuer à les utiliser.", 'Autoriser la mesure', 'Refuser la mesure', 'Confidentialité'],
  de: ['Analyse-Einstellungen', "Wir nutzen Google Analytics, um die Tools zu verbessern. Sie können die Analyse erlauben oder ablehnen und die Tools weiter nutzen.", 'Analyse erlauben', 'Analyse ablehnen', 'Datenschutz'],
  pt: ['Configurações de análise', "Usamos Google Analytics para melhorar as ferramentas. Pode permitir ou recusar a análise e continuar a utilizá-las.", 'Permitir análise', 'Recusar análise', 'Privacidade'],
};

function updateConsent(allowed: boolean, clearCookies = true) {
  const w = window as unknown as Record<string, any>;
  w[`ga-disable-${ID}`] = !allowed;
  w.dataLayer = w.dataLayer || [];
  // Consent commands must still reach the tag when internal-traffic tracking
  // suppresses event/config commands through window.gtag.
  function consentCommand(..._args: unknown[]) { w.dataLayer.push(arguments); }
  consentCommand('consent', 'update', { analytics_storage: allowed ? 'granted' : 'denied' });
  if (!allowed && clearCookies) {
    for (const part of document.cookie.split(';')) {
      const name = part.split('=')[0].trim();
      if (!/^_ga(?:_|$)/.test(name)) continue;
      const domains = ['', ...location.hostname.split('.').map((_, i, a) => a.slice(i).join('.'))];
      for (const domain of domains) document.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ''}; SameSite=Lax`;
    }
  }
}

export default function AnalyticsConsent({ lang = 'en', internal = false }: { lang?: Lang; internal?: boolean }) {
  const pathname = usePathname();
  const inEditor = internal && pathname === '/editor';
  const [choice, setChoice] = useState<boolean | null>(null);
  const [ready, setReady] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [requiresConsent, setRequiresConsent] = useState<boolean | null>(null);
  const [allowed, setAllowed] = useState(false);
  const [privacySignal, setPrivacySignal] = useState(false);
  const copy = COPY[lang];
  useEffect(() => {
    const refresh = () => {
    let saved: boolean | null = null;
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (typeof value?.allowed === 'boolean' && typeof value.at === 'number' && Date.now() - value.at >= 0 && Date.now() - value.at < MAX_AGE) saved = value.allowed;
    } catch { /* Storage can be disabled; the choice remains session-only. */ }
    setChoice(saved);
    setPrivacySignal((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true);
    setReady(true);
    };
    refresh();
    const sync = (event: StorageEvent) => { if (event.key === KEY || event.key === null) refresh(); };
    const showSettings = () => setSettingsOpen(true);
    window.addEventListener('storage', sync);
    window.addEventListener('la:open-analytics-settings', showSettings);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('la:open-analytics-settings', showSettings);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const timeout = setTimeout(() => controller.abort(), 2000);
    fetch('/api/analytics-region', { cache: 'no-store', signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Region unavailable');
        const result = await response.json();
        if (active) setRequiresConsent(result.requiresConsent !== false);
      })
      .catch(() => { if (active) setRequiresConsent(true); })
      .finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, []);

  useEffect(() => {
    const enabled = ready && !privacySignal && (choice === true || (choice !== false && requiresConsent === false));
    // During location lookup, stop transmission without erasing a returning
    // visitor's cookies. Delete them only once denial is actually resolved.
    const resolved = ready && (choice !== null || requiresConsent !== null || privacySignal);
    updateConsent(enabled, resolved);
    setAllowed(enabled);
  }, [ready, choice, requiresConsent, privacySignal]);

  function choose(allowed: boolean) {
    updateConsent(allowed && !privacySignal);
    try { localStorage.setItem(KEY, JSON.stringify({ allowed, at: Date.now() })); } catch { /* optional persistence */ }
    setChoice(allowed);
    setSettingsOpen(false);
  }

  // Unanswered analytics consent must not cover the editing application.
  // Its existing Settings menu can reopen this panel; analytics stays denied.
  const open = settingsOpen || (requiresConsent === true && choice === null && !inEditor && !privacySignal);

  return <>
    {allowed && <Script id="ga4-library" src={`https://www.googletagmanager.com/gtag/js?id=${ID}`} strategy="afterInteractive" onReady={() => {
      const w = window as unknown as Record<string, any>;
      if (w[`ga-disable-${ID}`]) return;
      w.gtag?.('js', new Date());
      // The Stripe success URL (a sign-in credential, see lib/analytics) can
      // reach this page as the referrer of a navigation out of the editor.
      w.gtag?.('config', ID, {
        send_page_view: !internal,
        page_location: redactCheckoutSecrets(window.location.href),
        page_referrer: redactCheckoutSecrets(document.referrer || ''),
      });
    }} />}
    {ready && (open ? <section className="analytics-choice" aria-label={copy[0]}>
      <strong>{copy[0]}</strong><p>{copy[1]}</p>
      {privacySignal && <p>{SIGNAL_COPY[lang]}</p>}
      <div><button type="button" disabled={privacySignal} onClick={() => choose(true)}>{copy[2]}</button><button type="button" onClick={() => choose(false)}>{copy[3]}</button>
        <a href={`${lang === 'ja' ? '' : `/${lang}`}/privacy`}>{copy[4]}</a></div>
    </section> : !inEditor && <button type="button" className="analytics-settings" onClick={() => setSettingsOpen(true)}>{copy[0]}</button>)}
    <style>{`.analytics-choice{position:fixed;bottom:8px;left:8px;right:8px;max-width:620px;max-height:35vh;overflow:auto;z-index:950;background:#fff;color:#222;border:1px solid #777;border-radius:8px;padding:10px 12px;box-shadow:0 3px 20px #0003;font:13px/1.5 system-ui}.analytics-choice p{margin:4px 0 8px}.analytics-choice div{display:flex;flex-wrap:wrap;gap:8px;align-items:center}.analytics-choice button,.analytics-settings{font:inherit;background:#fff;color:#222;border:1px solid #777;border-radius:6px;padding:8px 10px;cursor:pointer}.analytics-choice a{color:#624709;text-decoration:underline}.analytics-settings{position:fixed;bottom:8px;left:8px;z-index:900;font:12px system-ui}.analytics-choice button:focus-visible,.analytics-settings:focus-visible{outline:3px solid #3366cc;outline-offset:2px}`}</style>
  </>;
}
